import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CreditCard, Laptop, Users, Activity } from 'lucide-react'
import { licenseAPI, sessionAPI } from '../services/api'
import type { License, Session } from '../types'
import PaymentModal from '../components/PaymentModal'

const Dashboard: React.FC = () => {
  const { t } = useTranslation()
  const [stats, setStats] = useState({
    totalLicenses: 0,
    activeLicenses: 0,
    totalDevices: 0,
    totalSessions: 0
  })
  const [recentLicenses, setRecentLicenses] = useState<License[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<any>(null)

  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'))

  useEffect(() => {
    fetchDashboardData()
    
    // Sprawdź czy przekierowano po udanej płatności
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('payment') === 'success') {
      // Usuń parametr z URL
      window.history.replaceState({}, '', '/panel-clic/dashboard')
      // Pokaż powiadomienie
      alert('🎉 Płatność zakończona sukcesem! Sprawdź swój email i sekcję licencji.')
    }

    // Słuchaj zmian danych użytkownika
    const handleUserDataUpdate = () => {
      setUser(JSON.parse(localStorage.getItem('user') || '{}'))
    }
    
    window.addEventListener('userDataUpdated', handleUserDataUpdate)
    
    return () => {
      window.removeEventListener('userDataUpdated', handleUserDataUpdate)
    }
  }, [])

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true)
      
      const [licensesRes, sessionsRes] = await Promise.allSettled([
        licenseAPI.getMyLicenses(),
        sessionAPI.getMySessions()
      ])

      let licenses: License[] = []
      let sessions: Session[] = []
      let totalDevices = 0

      if (licensesRes.status === 'fulfilled') {
        const data = licensesRes.value.data as any
        if (Array.isArray(data)) {
          licenses = data
        } else if (data && data.licenses) {
          licenses = data.licenses
        } else if (data && data.history) {
          licenses = data.history
        } else {
          licenses = []
        }
        setRecentLicenses(licenses.slice(0, 3))
        
        // Calculate total devices from licenses
        totalDevices = licenses.reduce((sum, license) => sum + (license.active_devices || 0), 0)
      }

      if (sessionsRes.status === 'fulfilled') {
        sessions = sessionsRes.value.data || []
      }

      setStats({
        totalLicenses: licenses.length,
        activeLicenses: licenses.filter(l => l.status === 'active').length,
        totalDevices: totalDevices,
        totalSessions: sessions.length
      })

    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-900/20 text-green-400 border-green-900/50'
      case 'expired':
        return 'bg-red-900/20 text-red-400 border-red-900/50'
      case 'cancelled':
        return 'bg-gray-900/20 text-gray-400 border-gray-900/50'
      default:
        return 'bg-yellow-900/20 text-yellow-400 border-yellow-900/50'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">{t('dashboard.title')}</h1>
        <p className="text-gray-400">{t('dashboard.welcome')}, {user.name || user.email || 'User'}!</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center">
            <div className="p-2 bg-primary/10 rounded-lg">
              <CreditCard className="h-6 w-6 text-primary" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">{t('dashboard.activeLicenses')}</p>
              <p className="text-2xl font-bold text-white">
                {isLoading ? '...' : `${stats.activeLicenses}/${stats.totalLicenses}`}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center">
            <div className="p-2 bg-blue-600/10 rounded-lg">
              <Laptop className="h-6 w-6 text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">{t('dashboard.totalDevices')}</p>
              <p className="text-2xl font-bold text-white">
                {isLoading ? '...' : stats.totalDevices}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center">
            <div className="p-2 bg-purple-600/10 rounded-lg">
              <Users className="h-6 w-6 text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">{t('dashboard.totalSessions')}</p>
              <p className="text-2xl font-bold text-white">
                {isLoading ? '...' : stats.totalSessions}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center">
            <div className="p-2 bg-green-600/10 rounded-lg">
              <Activity className="h-6 w-6 text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">{t('dashboard.recentActivity')}</p>
              <p className="text-2xl font-bold text-white">
                {stats.totalDevices > 0 ? '●' : '○'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Available Packages */}
      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h3 className="text-xl font-semibold text-white mb-4">{t('dashboard.availablePackages')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          
          {/* Standard Package */}
          <div className="bg-dark-700 rounded-lg p-6 border border-dark-500 hover:border-primary/50 transition-colors">
            <div className="text-center">
              <h4 className="text-lg font-semibold text-white mb-2">{t('dashboard.standard')}</h4>
              <div className="text-3xl font-bold text-primary mb-2">24.99 <span className="text-sm text-gray-400">EUR</span></div>
              <p className="text-sm text-gray-400 mb-4">{t('dashboard.perYear')}</p>
              <ul className="text-sm text-gray-300 space-y-2 mb-6">
                <li>• 5 {t('dashboard.devices')}</li>
                <li>• {t('dashboard.basicFeatures')}</li>
                <li>• {t('dashboard.emailSupport')}</li>
                <li>• {t('dashboard.slideNavigation')}</li>
              </ul>
              <button 
                onClick={() => {
                  setSelectedPlan({
                    id: 6,
                    name: 'Standard',
                    price_monthly: 499, // €4.99
                    price_yearly: 2499, // €24.99
                    features: '5 devices, basic features, email support'
                  });
                  setShowPaymentModal(true);
                }}
                className="w-full py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                {t('dashboard.chooseStandard')}
              </button>
            </div>
          </div>

          {/* Pro Package */}
          <div className="bg-dark-700 rounded-lg p-6 border border-primary relative">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-primary text-white px-3 py-1 rounded-full text-xs font-medium">{t('dashboard.popular')}</span>
            </div>
            <div className="text-center">
              <h4 className="text-lg font-semibold text-white mb-2">{t('dashboard.pro')}</h4>
              <div className="text-3xl font-bold text-primary mb-2">59.99 <span className="text-sm text-gray-400">EUR</span></div>
              <p className="text-sm text-gray-400 mb-4">{t('dashboard.perYear')}</p>
              <ul className="text-sm text-gray-300 space-y-2 mb-6">
                <li>• 50 {t('dashboard.devices')}</li>
                <li>• {t('dashboard.allFeatures')}</li>
                <li>• {t('dashboard.laserPointer')}</li>
                <li>• {t('dashboard.analytics')}</li>
                <li>• {t('dashboard.prioritySupport')}</li>
              </ul>
              <button 
                onClick={() => {
                  setSelectedPlan({
                    id: 7,
                    name: 'Pro',
                    price_monthly: 799, // €7.99
                    price_yearly: 5999, // €59.99
                    features: '50 devices, all features, priority support'
                  });
                  setShowPaymentModal(true);
                }}
                className="w-full py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                {t('dashboard.choosePro')}
              </button>
            </div>
          </div>

          {/* Unlimited Package */}
          <div className="bg-dark-700 rounded-lg p-6 border border-primary">
            <div className="text-center">
              <h4 className="text-lg font-semibold text-white mb-2">{t('dashboard.unlimited')}</h4>
              <div className="text-3xl font-bold text-primary mb-2">699.00 <span className="text-sm text-gray-400">EUR</span></div>
              <p className="text-sm text-gray-400 mb-4">{t('dashboard.lifetime')}</p>
              <ul className="text-sm text-gray-300 space-y-2 mb-6">
                <li>• {t('dashboard.unlimitedDevices')}</li>
                <li>• {t('dashboard.allProFeatures')}</li>
                <li>• {t('dashboard.whiteLabel')}</li>
                <li>• {t('dashboard.apiAccess')}</li>
                <li>• {t('dashboard.lifetimeAccess')}</li>
              </ul>
              <button 
                onClick={() => {
                  setSelectedPlan({
                    id: 8,
                    name: 'Unlimited',
                    price_monthly: 69900,
                    price_yearly: 69900,
                    features: 'Unlimited devices, all features, lifetime access'
                  });
                  setShowPaymentModal(true);
                }}
                className="w-full py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors"
              >
                {t('payment.buyNow')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Licenses */}
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <h3 className="text-lg font-semibold text-white mb-4">{t('dashboard.recentLicenses')}</h3>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-dark-700 h-16 rounded"></div>
              ))}
            </div>
          ) : recentLicenses.length > 0 ? (
            <div className="space-y-3">
              {recentLicenses.map((license) => (
                <div key={license.id} className="flex items-center justify-between p-3 bg-dark-700 rounded-lg">
                  <div>
                    <p className="font-medium text-white">{license.plan_name}</p>
                    <p className="text-sm text-gray-400">{license.license_key}</p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getStatusColor(license.status)}`}>
                      {t(`licenses.${license.status}`)}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDate(license.valid_to)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">{t('licenses.noLicenses')}</p>
          )}
        </div>

        {/* Device Summary */}
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <h3 className="text-lg font-semibold text-white mb-4">{t('dashboard.devicesSummary')}</h3>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-dark-700 h-16 rounded"></div>
              ))}
            </div>
          ) : recentLicenses.length > 0 ? (
            <div className="space-y-3">
              {recentLicenses.map((license) => (
                <div key={license.id} className="flex items-center justify-between p-3 bg-dark-700 rounded-lg">
                  <div>
                    <p className="font-medium text-white">{license.plan_name}</p>
                    <p className="text-sm text-gray-400">{license.license_key?.substring(0, 19)}...</p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium border bg-blue-900/20 text-blue-400 border-blue-900/50">
                      {license.active_devices || 0}/{license.max_devices} urządzeń
                    </span>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDate(license.valid_to)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">{t('devices.noDevices')}</p>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {selectedPlan && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedPlan(null);
          }}
          plan={selectedPlan}
          onSuccess={() => {
            setShowPaymentModal(false);
            setSelectedPlan(null);
            fetchDashboardData();
          }}
        />
      )}
    </div>
  )
}

export default Dashboard